import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isDespiaRuntime } from '@/hooks/use-despia';

/**
 * Parse buxbox://oauth/auth?access_token=...&refresh_token=... deeplink
 */
const buildInternalAuthUrl = (incomingUrl: string): string | null => {
  try {
    const parsedUrl = new URL(incomingUrl);
    const isOauthCallback = parsedUrl.hostname === 'oauth' && parsedUrl.pathname === '/auth';

    if (!isOauthCallback) return null;

    const accessToken = parsedUrl.searchParams.get('access_token');
    const refreshToken = parsedUrl.searchParams.get('refresh_token');
    const error = parsedUrl.searchParams.get('error');

    if (error) {
      return `/auth?error=${encodeURIComponent(error)}`;
    }

    if (accessToken && refreshToken) {
      return `/auth?access_token=${encodeURIComponent(accessToken)}&refresh_token=${encodeURIComponent(refreshToken)}`;
    }

    return '/auth';
  } catch {
    return null;
  }
};

/**
 * Hook to handle deeplink callbacks from native platforms.
 * Uses Capacitor App plugin on native, or Despia message events.
 * Only needed for Android oauth:// redirect flow.
 */
export const useCapacitorInit = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const isDespia = isDespiaRuntime();

    // Only needed in native environments
    if (!isDespia) {
      // Try Capacitor native check
      try {
        const { Capacitor } = require('@capacitor/core');
        if (!Capacitor.isNativePlatform()) return;
      } catch {
        return;
      }
    }

    let cleanup: (() => void) | undefined;

    const handleIncomingUrl = (incomingUrl?: string | null) => {
      if (!incomingUrl) return;
      const internalUrl = buildInternalAuthUrl(incomingUrl);
      if (internalUrl) {
        navigate(internalUrl, { replace: true });
      }
    };

    const initializeListeners = async () => {
      try {
        const { App } = await import('@capacitor/app');

        const urlListener = await App.addListener('appUrlOpen', (event) => {
          handleIncomingUrl(event.url);
        });

        const stateListener = await App.addListener('appStateChange', () => {});

        // Check if app was opened via deeplink
        const launchUrl = await App.getLaunchUrl();
        handleIncomingUrl(launchUrl?.url);

        cleanup = () => {
          void urlListener.remove();
          void stateListener.remove();
        };
      } catch {
        // Capacitor App plugin not available — expected on web/Despia iOS
      }
    };

    initializeListeners();

    return () => {
      cleanup?.();
    };
  }, [navigate]);

  return {
    isNativePlatform: isDespiaRuntime(),
  };
};
